import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Stack, StackProps, Typography } from '@mui/material';
import dayjs from 'dayjs';

import {
	tripFeedbackManual,
	useBusinessLayer,
	useMoplaLegsFeedbacks,
} from '@mopla/business-logic';
import { BookingStateEnum, PassengerBookingView } from '@mopla/data-models';
import { getItineraryTimingV2, getMoplaLegs } from '@mopla/utils';

import { Loader } from '../../atoms/loader/Loader';

import { NonRatedContent } from './NonRatedContent';
import { RatedContent } from './RatedContent';

export interface IMplFeedbackResultProps extends StackProps {
	booking: PassengerBookingView;
	showRateAction?: boolean;
}

export const MplFeedbackResult: FC<IMplFeedbackResultProps> = memo(
	function MplFeedbackResult({
		booking,
		showRateAction = true,
		...stackProps
	}) {
		const { dispatch } = useBusinessLayer();
		const { t } = useTranslation('booking');

		const moplaLegs = useMemo(
			() => getMoplaLegs(booking.itinerary.legs),
			[booking]
		);

		const moplaLegsIds = useMemo(
			() => moplaLegs.map(({ legId }) => legId),
			[moplaLegs]
		);

		const {
			data: feedbackData,
			isLoading,
			isError,
		} = useMoplaLegsFeedbacks(moplaLegsIds);

		const isRated = Boolean(feedbackData && feedbackData[0]?.feedback);

		// Can be rated within one month after the trip ends
		const isAbleToBeRated = useMemo(() => {
			if (!moplaLegs.length) return false;

			const { effectiveEndDateObj } = getItineraryTimingV2(moplaLegs);

			return (
				showRateAction &&
				booking.bookingStatus === BookingStateEnum.FINISHED &&
				!isRated &&
				dayjs(effectiveEndDateObj).add(1, 'month').isAfter(dayjs())
			);
		}, [booking.bookingStatus, isRated, moplaLegs, showRateAction]);

		const onRatingClick = () => {
			feedbackData && dispatch(tripFeedbackManual(feedbackData));
		};

		/** {feedbackData} keeps the previously opened booking's result, so it has
		 * to be checked against the legs of the current one. Empty for bookings
		 * without ratable legs (e.g. school rides).
		 * */
		if (isError || !feedbackData || !moplaLegsIds.length) {
			return null;
		}

		return (
			<Stack {...stackProps}>
				<Typography
					variant="body2"
					fontWeight={600}
					children={t('feedback.saved_feedback_title')}
					pb={0.5}
				/>
				<Collapse in={isLoading}>
					<Loader />
				</Collapse>
				<Collapse in={!isLoading}>
					{feedbackData &&
						(isRated ? (
							<RatedContent feedbackDataArray={feedbackData} />
						) : (
							<NonRatedContent
								feedbackDataArray={feedbackData}
								onRatingClick={onRatingClick}
								isAbleToBeRated={isAbleToBeRated}
							/>
						))}
				</Collapse>
			</Stack>
		);
	}
);
